import { useCallback, useMemo, useState } from "react";
import {
  Outcome,
  ProductTree,
  SessionGuardrailResult,
  SessionGuardrailTree,
} from "@financial-advisory/data-access/api-types";
import { config } from "@targeted-support/config";
import { useBranding } from "@targeted-support/ui/contexts";
import { UnlikelyMark } from "@targeted-support/ui/features/overview";
import { User } from "lucide-react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";
import { type QuestionAnswer } from "@unlikelyai-magic/utils";
import { GraphModal } from "./GraphModal";
import { guardrailPassed } from "./guardrailUtils";
import { GuardrailTimelineModal } from "./GuardrailTimelineModal";
import { MarkdownContent } from "./MarkdownContent";
import { NumberBadge } from "./NumberBadge";
import { type ConversationPair } from "./types";

/**
 * ConversationItem component displays a single conversation pair (user message + system response)
 */
interface ConversationItemProps {
  conversation: ConversationPair;
  isSelected: boolean;
  onSelect: () => void;
  itemRef: (el: HTMLDivElement | null) => void;
}

const getRelevantIterations = (
  lastGuardrailIteration: SessionGuardrailResult | undefined,
  orderedIterations: SessionGuardrailResult[]
): SessionGuardrailResult[] => {
  if (!lastGuardrailIteration) {
    return [];
  }
  if (!lastGuardrailIteration.sequence_id || orderedIterations.length === 1) {
    return [lastGuardrailIteration];
  }
  return orderedIterations.filter(
    (iteration) => iteration.sequence_id === lastGuardrailIteration.sequence_id
  );
};
/**
 * @param guardrailResult - The guardrail result to get the trees for
 * @param isPassed - True if looking for passed guardrails, false if looking for failed guardrails
 */
const getGuardrailTreesForOutcome = (
  guardrailResult: SessionGuardrailResult | undefined,
  isPassed: boolean
): SessionGuardrailTree[] => {
  if (!guardrailResult) {
    return [];
  }
  if (guardrailResult.guardrail_trees.length === 0) {
    return [];
  }
  return guardrailResult.guardrail_trees.filter((tree) =>
    isPassed ? guardrailPassed(tree) : !guardrailPassed(tree)
  );
};

const getOutputGuardrailData = (conversation: ConversationPair) => {
  const botMessage = conversation.systemResponse;

  const orderedIterations =
    conversation.outputGuardrailTrees
      ?.slice()
      .sort((a, b) => a.sequence_number - b.sequence_number) ?? [];

  const lastGuardrailIteration = orderedIterations.at(-1);

  const relevantIterations = getRelevantIterations(
    lastGuardrailIteration,
    orderedIterations
  );

  const lastGuardrailedMessage =
    lastGuardrailIteration?.message?.content ?? "";

  const guardrails = lastGuardrailIteration?.guardrail_trees ?? [];

  const failedGuardrails = getGuardrailTreesForOutcome(
    lastGuardrailIteration,
    false
  );

  const passedGuardrails = getGuardrailTreesForOutcome(
    lastGuardrailIteration,
    true
  );

  // If the last guardrailed message is not the bot message, then its compliant by design
  const byDesignMessage = botMessage !== lastGuardrailedMessage;

  return {
    byDesignMessage,
    guardrails,
    failedGuardrails,
    passedGuardrails,
    guardrailIterations: relevantIterations,
  };
};

const getInputGuardrailData = (conversation: ConversationPair) => {
  const orderedIterations =
    conversation.inputGuardrailTrees
      ?.slice()
      .sort((a, b) => a.sequence_number - b.sequence_number) ?? [];

  const lastGuardrailIteration = orderedIterations.at(-1);

  const relevantIterations = getRelevantIterations(
    lastGuardrailIteration,
    orderedIterations
  );

  const guardrails = lastGuardrailIteration?.guardrail_trees ?? [];

  const failedGuardrails = getGuardrailTreesForOutcome(
    lastGuardrailIteration,
    false
  );

  const passedGuardrails = getGuardrailTreesForOutcome(
    lastGuardrailIteration,
    true
  );

  return {
    guardrails,
    failedGuardrails,
    passedGuardrails,
    guardrailIterations: relevantIterations,
  };
};

/**
 * The verdict pill under a message. The UnlikelyAI mark and a divider make it
 * clear the verdict is ours, not the agent's, and the shields say what happened.
 */
const VerdictPill = ({
  onClick,
  label,
  children,
}: {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  label: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="flex h-6 items-center gap-2 rounded-full border border-brand-border bg-brand-card px-2.5 shadow-sm transition-colors hover:border-unlikely focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-unlikely/40"
  >
    <UnlikelyMark size={0.6875} />
    <span className="h-3 w-px bg-brand-border" aria-hidden={true} />
    {children}
  </button>
);

export const ConversationItem = ({
  conversation,
  isSelected,
  onSelect,
  itemRef,
}: ConversationItemProps) => {
  const [gruardrailToShow, setGuardrailToShow] = useState<string | null>(null);
  const [isOutputTimelineOpen, setIsOutputTimelineOpen] = useState(false);
  const [isInputTimelineOpen, setIsInputTimelineOpen] = useState(false);
  const { Logo, tenantId } = useBranding();

  const handleCloseModal = useCallback(() => {
    setGuardrailToShow(null);
  }, []);

  // Output guardrail data (for system/assistant responses)
  const {
    byDesignMessage,
    guardrails: outputGuardrails,
    guardrailIterations: outputGuardrailIterations,
  } = getOutputGuardrailData(conversation);

  // Input guardrail data (for user messages)
  const {
    failedGuardrails: inputFailedGuardrails,
    guardrailIterations: inputGuardrailIterations,
  } = getInputGuardrailData(conversation);

  const guardrailTreesForLookup = useMemo(
    () =>
      outputGuardrailIterations.flatMap(
        (iteration) => iteration.guardrail_trees ?? []
      ),
    [outputGuardrailIterations]
  );

  const hasOutputRephraseHistory =
    outputGuardrailIterations.length > 1 &&
    outputGuardrailIterations[0].sequence_id !== null &&
    outputGuardrailIterations[0].sequence_id !== undefined;

  const hasInputGuardrailResults = inputGuardrailIterations.length > 0;

  const showGraphModal = () => {
    if (gruardrailToShow) {
      const guardrailTree = guardrailTreesForLookup.find(
        (tree) => tree.id === gruardrailToShow
      );
      if (!guardrailTree?.guardrail_tree) return null;
      const passed = guardrailPassed(guardrailTree);
      const guardrailName = guardrailTree.guardrail_tree.name;
      const explanation = passed
        ? <>The agent message did not trigger the <strong>{guardrailName}</strong> guardrail.</>
        : <>The agent message did trigger the <strong>{guardrailName}</strong> guardrail.</>;
      return (
        <GraphModal
          key={gruardrailToShow}
          productTree={{
            ...guardrailTree.guardrail_tree,
            description: "",
            preferenceRank: 0
          }}
          questionAnswers={
            guardrailTree.leaf_nodes?.map((node) => {
              return {
                question: node.question,
                value: node.value === Outcome.TRUE ? "TRUE" : "FALSE",
                reasoning: node.reasoning,
              } as QuestionAnswer;
            }) ?? []
          }
          onClose={handleCloseModal}
          explanation={explanation}
        />
      );
    }
    return null;
  };

  const renderOutputShieldSequence = () => {
    if (outputGuardrailIterations.length === 0) return null;
    // A message the agent never had to rewrite still passed, so it gets the
    // single green shield rather than the verdict of its last iteration.
    if (byDesignMessage) {
      return (
        <ShieldCheck
          className="h-4 w-4 text-green-600"
          fill="currentColor"
          stroke="white"
        />
      );
    }
    return (
      <span className="flex items-center gap-1.5">
        {outputGuardrailIterations.map((iteration, idx) => {
          const failed =
            iteration.guardrail_trees?.some((tree) => !guardrailPassed(tree)) ??
            false;
          const Icon = failed ? ShieldAlert : ShieldCheck;
          const colorClass = failed ? "text-red-500" : "text-green-600";
          return (
            <span key={iteration.id} className="flex items-center gap-1.5">
              <Icon
                className={`h-4 w-4 ${colorClass}`}
                fill="currentColor"
                stroke="white"
              />
              {idx < outputGuardrailIterations.length - 1 && (
                <span className="text-[10px] leading-none text-brand-muted">
                  •••
                </span>
              )}
            </span>
          );
        })}
      </span>
    );
  };

  const renderInputShieldIcon = () => {
    const hasFailed = inputFailedGuardrails.length > 0;
    const Icon = hasFailed ? ShieldAlert : ShieldCheck;
    const colorClass = hasFailed ? "text-red-500" : "text-green-600";
    return (
      <Icon
        className={`h-4 w-4 ${colorClass}`}
        fill="currentColor"
        stroke="white"
      />
    );
  };

  const handleOutputGuardrailButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    setIsOutputTimelineOpen(true);
  };

  const handleInputGuardrailButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    setIsInputTimelineOpen(true);
  };

  return (
    <>
      {isOutputTimelineOpen && (
        <GuardrailTimelineModal
          iterations={outputGuardrailIterations}
          showTimeline={hasOutputRephraseHistory}
          onClose={() => setIsOutputTimelineOpen(false)}
          title="Output Guardrail Timeline"
        />
      )}
      {isInputTimelineOpen && (
        <GuardrailTimelineModal
          iterations={inputGuardrailIterations}
          showTimeline={false}
          onClose={() => setIsInputTimelineOpen(false)}
          title="Input Guardrail Timeline"
          isInput={true}
        />
      )}
      {showGraphModal()}
      <div
        ref={itemRef}
        onClick={onSelect}
        className={cn(
          "cursor-pointer block w-full rounded-lg p-3 text-left transition-all border-2",
          isSelected
            ? "bg-brand-primary/5 border-brand-primary shadow-lg"
            : "border-transparent hover:bg-brand-primary/5 hover:border-brand-border"
        )}
      >
        {conversation.isLoadingResponse && !conversation.systemResponse.trim() && (
          <div className="flex w-full gap-2 text-left mb-3 items-start">
            <div className="flex-shrink-0 mt-0.5">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary/10 border border-brand-border">
                <Logo
                  size={0.9}
                  fill={
                    tenantId === config.DEMO_ORGANISATION_ID
                      ? "#B11082"
                      : undefined
                  }
                />
              </div>
            </div>
            <div className="rounded-lg bg-brand-primary/5 px-4 py-2">
              <div className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-muted animate-bounce [animation-delay:0ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-brand-muted animate-bounce [animation-delay:150ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-brand-muted animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}

        {/* System response - only show if present */}
        {conversation.systemResponse.trim() !== "" && (
          <div className="flex w-full gap-2 text-left mb-3 items-start">
            <div className="flex-shrink-0 mt-0.5">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary/10 border border-brand-border">
                <Logo
                  size={0.9}
                  fill={
                    tenantId === config.DEMO_ORGANISATION_ID
                      ? "#B11082"
                      : undefined
                  }
                />
              </div>
            </div>
            <div className="flex-1">
                <>
                  <div className="text-sm leading-relaxed text-brand-foreground">
                    <MarkdownContent content={conversation.systemResponse} />
                  </div>
                  {(outputGuardrails.length > 0 ||
                    outputGuardrailIterations.length > 0) && (
                      <div className="mt-2">
                        <VerdictPill
                          onClick={handleOutputGuardrailButtonClick}
                          label="Output guardrail results for this reply"
                        >
                          {renderOutputShieldSequence()}
                        </VerdictPill>
                      </div>
                    )}
                </>
            </div>
          </div>
        )}

        {/* User typing indicator */}
        {conversation.isUserTyping && conversation.userMessage.trim() === "" && (
          <div className="flex justify-end gap-2 items-start">
            <div className="flex items-start gap-2">
              <div className="rounded-lg bg-brand-primary/10 px-4 py-2">
                <div className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-muted animate-bounce [animation-delay:0ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-muted animate-bounce [animation-delay:150ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-muted animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="flex h-6 w-6 items-center justify-center mt-[-0.1rem] rounded-full bg-brand-muted/10 border border-brand-border">
                  <User className="h-3 w-3 text-brand-muted" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User message - only show if present */}
        {conversation.userMessage.trim() !== "" && (
          <div className={cn("flex justify-end gap-2 items-start")}>
            <div className="flex flex-col items-end gap-2 max-w-[80%]">
              <div className="flex items-start gap-2">
                <div className="w-full rounded-lg bg-brand-primary/10  text-sm text-brand-foreground">
                  {conversation.userMessage}
                </div>
                <div className="flex-shrink-0">
                  <div className="flex h-6 w-6 items-center justify-center mt-[-0.1rem] rounded-full bg-brand-muted/10 border border-brand-border">
                    <User className="h-3 w-3 text-brand-muted" />
                  </div>
                </div>
              </div>
              {/* Input guardrail button - only show if there are input guardrail results */}
              {hasInputGuardrailResults && (
                <VerdictPill
                  onClick={handleInputGuardrailButtonClick}
                  label="Input guardrail results for this message"
                >
                  {renderInputShieldIcon()}
                </VerdictPill>
              )}
              {conversation.index > 0 && (
                <NumberBadge
                  number={conversation.index + 1}
                  isSelected={isSelected}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
